import { useEffect, useRef, useState } from 'react';
import axios from '../axiosInstance';
import { FaCheck, FaTimes, FaUserShield } from 'react-icons/fa';
import { PIP_ASSOCIATE_OPTIONS, RISK_LABEL } from './riskOptions';

// PIP check on the capture form: as the client's name / ID is typed in, the
// PIP database is searched for possible PIPs and possible associates of PIPs.
// Each possible match is accepted or rejected; the decisions give the PIP and
// Associate of a PIP inputs (the server checks them again on save).

const matchKey = (m) => `${m.source}:${m.record_id}`;
const lookupKey = (v) => JSON.stringify([
  v.first_name, v.middle_name, v.last_name, v.maiden_name, v.id_number
].map((x) => String(x || '').trim().toLowerCase()));
const hasSearchable = (v) =>
  (String(v.first_name || '').trim() && String(v.last_name || '').trim()) ||
  String(v.id_number || '').trim().length >= 2;

/**
 * @param ind       the form's individual fields
 * @param enabled   false for legal persons
 * @param saved     customer.pip_matches in edit mode (earlier decisions)
 */
export function usePipCheck(ind, enabled, saved) {
  const [result, setResult] = useState({ pips: [], associates: [] });
  const [checkedFor, setCheckedFor] = useState(null); // lookupKey the result belongs to
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [decisions, setDecisions] = useState(() =>
    Object.fromEntries((saved || []).map((m) => [matchKey(m), m.decision]))
  );
  const [nonce, setNonce] = useState(0); // Try again
  const seq = useRef(0);

  const lookup = {
    first_name: ind.first_name, middle_name: ind.middle_name, last_name: ind.last_name,
    maiden_name: ind.maiden_name, id_number: ind.id_number
  };
  const key = lookupKey(lookup);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!hasSearchable(lookup)) {
      seq.current += 1;
      setLoading(false);
      setResult({ pips: [], associates: [] });
      setCheckedFor(key);
      setError(null);
      return undefined;
    }
    const mine = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const body = Object.fromEntries(
          Object.entries(lookup).map(([k, v]) => [k, String(v || '').trim() || null])
        );
        const res = await axios.post('/customers/pip-check', body);
        if (mine !== seq.current) return;
        setResult(res.data || { pips: [], associates: [] });
        setCheckedFor(key);
        setError(null);
      } catch (err) {
        if (mine !== seq.current) return;
        setError(err.response?.data?.error?.message || err.message);
        setCheckedFor(null);
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, nonce]);

  const candidates = [...result.pips, ...result.associates];
  const decisionOf = (m) => decisions[matchKey(m)] || null;
  const decide = (m, d) => setDecisions((prev) => ({ ...prev, [matchKey(m)]: d }));

  const acceptedPips = result.pips.filter((m) => decisionOf(m) === 'accepted');
  const acceptedAssociates = result.associates.filter((m) => decisionOf(m) === 'accepted');
  const undecided = candidates.filter((m) => !decisionOf(m));

  return {
    result, candidates, loading, error, decide, decisionOf,
    upToDate: checkedFor === key && !loading && !error,
    acceptedPips, acceptedAssociates, undecided,
    isPip: acceptedPips.length > 0,
    isAssociate: acceptedAssociates.length > 0,
    retry: () => setNonce((n) => n + 1),
    payload: () => candidates.map((m) => ({ source: m.source, record_id: m.record_id, decision: decisionOf(m) }))
  };
}

const scorePill = (s) =>
  `un-meta__pill ${s >= 90 ? 'cust-pill--risk-high' : s >= 70 ? 'cust-pill--risk-medium' : 'cust-pill--risk-low'}`;

function MatchRow({ m, check, extra }) {
  const d = check.decisionOf(m);
  return (
    <tr style={{ borderBottom: '1px solid var(--border-color,#eef0f3)' }}>
      <td style={{ padding: '0.5rem' }}>
        <div><strong>{m.name}</strong></div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary,#607d8b)' }}>{extra}</div>
        {m.surname_only && (
          <div style={{ fontSize: '0.72rem', color: '#b26a00', marginTop: '0.15rem' }}>
            Only the surname matches. Accept if this client is this person; reject if they just share the surname.
          </div>
        )}
      </td>
      <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
        <span className={scorePill(m.score)}>{m.score} · {m.matched_on}</span>
      </td>
      <td style={{ padding: '0.5rem', whiteSpace: 'nowrap', textAlign: 'right' }}>
        <button
          type="button"
          className={d === 'rejected' ? 'export-button' : 'btn-cancel'}
          style={{ padding: '0.2rem 0.55rem', fontSize: '0.75rem', marginRight: '0.3rem' }}
          aria-pressed={d === 'rejected'}
          onClick={() => check.decide(m, 'rejected')}
        >
          <FaTimes /> Reject
        </button>
        <button
          type="button"
          className={d === 'accepted' ? 'export-button' : 'btn-cancel'}
          style={{ padding: '0.2rem 0.55rem', fontSize: '0.75rem' }}
          aria-pressed={d === 'accepted'}
          onClick={() => check.decide(m, 'accepted')}
        >
          <FaCheck /> Accept
        </button>
        <div style={{ fontSize: '0.7rem', marginTop: '0.2rem', color: d ? (d === 'accepted' ? '#d32f2f' : 'var(--text-secondary,#607d8b)') : '#d32f2f' }}>
          {d === 'accepted' ? 'Accepted — same person' : d === 'rejected' ? 'Rejected — not the same person' : 'Not decided'}
        </div>
      </td>
    </tr>
  );
}

function MatchTable({ title, rows, check, describe }) {
  return (
    <div style={{ marginBottom: '0.9rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.85rem', margin: '0.4rem 0' }}>
        {title} ({rows.length})
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary,#607d8b)' }}>None found.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color,#e3e6ea)', textAlign: 'left' }}>
                <th style={{ padding: '0.45rem 0.5rem' }}>Record in the PIP database</th>
                <th style={{ padding: '0.45rem 0.5rem' }}>Match</th>
                <th style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>Decision</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => <MatchRow key={matchKey(m)} m={m} check={check} extra={describe(m)} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** The panel shown in the Client Risk Rating Inputs section. */
export default function PipCheckPanel({ check, relationship, onRelationship, errors, errStyle }) {
  const { result, loading, error, upToDate, isPip, isAssociate, candidates } = check;
  const found = candidates.length;

  return (
    <div className="form-group" data-field="pip_check" tabIndex={-1} style={{ gridColumn: '1 / -1' }}>
      <label className="required"><FaUserShield /> PIP check</label>
      <div
        style={{
          border: '1px solid var(--border-color,#e3e6ea)', padding: '0.75rem',
          ...(errors.pip_check ? errStyle : {})
        }}
      >
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary,#607d8b)', marginBottom: '0.5rem' }}>
          Checked automatically against the PIP database from the name and ID above.
          Accept a record if it is this client; reject it if it isn't.
        </div>

        {loading && <div style={{ fontSize: '0.85rem' }}>Checking the PIP database…</div>}
        {!loading && error && (
          <div style={{ fontSize: '0.85rem', color: '#d32f2f' }}>
            PIP check failed: {error}{' '}
            <button type="button" className="btn-cancel" style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }} onClick={check.retry}>
              Try again
            </button>
          </div>
        )}
        {!loading && !error && upToDate && found === 0 && (
          <div style={{ fontSize: '0.85rem' }}>
            <span className="un-meta__pill cust-pill--risk-low">No possible PIP or associate of a PIP found</span>
          </div>
        )}
        {!loading && !error && upToDate && found > 0 && (
          <>
            <div className="alert-banner alert-banner--danger" style={{ marginBottom: '0.6rem' }}>
              <span className="alert-banner__body">
                Possible {result.pips.length ? 'PIP' : ''}{result.pips.length && result.associates.length ? ' / ' : ''}
                {result.associates.length ? 'Associate of a PIP' : ''} — review each record below.
              </span>
            </div>
            <MatchTable
              title="Possible PIP"
              rows={result.pips}
              check={check}
              describe={(m) => [m.category, m.pip_type, m.position, m.institution].filter(Boolean).join(' · ')}
            />
            <MatchTable
              title="Possible Associate of a PIP"
              rows={result.associates}
              check={check}
              describe={(m) => `${m.relationship_type || 'Associate'} of ${m.related_pip.name}${m.related_pip.pip_type ? ` (${m.related_pip.pip_type})` : ''}`}
            />
          </>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end', marginTop: '0.4rem' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)' }}>PIP</div>
            <span className={`un-meta__pill ${isPip ? 'cust-pill--risk-high' : 'cust-pill--risk-low'}`}>
              {isPip ? `Yes (${RISK_LABEL.high} risk)` : `No (${RISK_LABEL.low} risk)`}
            </span>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)' }}>Associate of a PIP</div>
            {isAssociate ? (
              <select
                data-field="pip_associate_relationship"
                value={relationship}
                style={errors.pip_associate_relationship ? errStyle : undefined}
                onChange={(e) => onRelationship(e.target.value)}
              >
                <option value="">Select relationship…</option>
                {PIP_ASSOCIATE_OPTIONS.filter((o) => o.v !== 'no').map((o) => (
                  <option key={o.v} value={o.v}>{o.label} ({RISK_LABEL[o.risk]} risk)</option>
                ))}
              </select>
            ) : (
              <span className="un-meta__pill cust-pill--risk-low">No ({RISK_LABEL.low} risk)</span>
            )}
          </div>
        </div>
        {errors.pip_associate_relationship && <span className="form-error">{errors.pip_associate_relationship}</span>}
      </div>
      {errors.pip_check && <span className="form-error">{errors.pip_check}</span>}
    </div>
  );
}
